import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getBackendUrl } from '../utils/api';
import {
  Activity,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Download,
  Pause,
  Server,
  Zap,
} from 'lucide-react';

const BACKEND_URL = getBackendUrl();
const POLL_INTERVAL_MS = 5000;

/**
 * v2.9.200 — Layered Telemetry Dashboard
 *
 * Unified saturation view across every concurrency gate in the
 * stream-monitor pipeline. Pulls /api/admin/diagnostic/layered-telemetry
 * every 5s so the operator can see exactly which layer is the current
 * bottleneck (polling gate, audio processing, fingerprint, background
 * tasks, listener pool …) without reading three different endpoints.
 */
const severityStyles = {
  ok: {
    bar: 'bg-emerald-500',
    track: 'bg-emerald-900/40',
    badge: 'bg-emerald-900/50 text-emerald-200 border-emerald-700',
    icon: CheckCircle2,
    label: 'OK',
  },
  elevated: {
    bar: 'bg-amber-400',
    track: 'bg-amber-900/30',
    badge: 'bg-amber-900/50 text-amber-200 border-amber-700',
    icon: Activity,
    label: 'Αυξημένο',
  },
  warning: {
    bar: 'bg-orange-500',
    track: 'bg-orange-900/30',
    badge: 'bg-orange-900/50 text-orange-200 border-orange-700',
    icon: AlertTriangle,
    label: 'Προσοχή',
  },
  critical: {
    bar: 'bg-red-500',
    track: 'bg-red-900/30',
    badge: 'bg-red-900/60 text-red-200 border-red-700',
    icon: AlertCircle,
    label: 'Κρίσιμο',
  },
};

const LayerRow = ({ layer }) => {
  const sev = severityStyles[layer.severity] || severityStyles.ok;
  const Icon = sev.icon;
  const pct = Math.min(100, Math.max(0, layer.saturation_pct || 0));
  return (
    <div
      className="bg-gray-800 rounded-lg p-4 border border-gray-700"
      data-testid={`layered-telemetry-row-${layer.key}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${
            layer.severity === 'critical'
              ? 'text-red-400'
              : layer.severity === 'warning'
                ? 'text-orange-400'
                : layer.severity === 'elevated'
                  ? 'text-amber-400'
                  : 'text-emerald-400'
          }`} />
          <span className="text-sm font-medium text-white">{layer.label}</span>
          {!layer.active && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-400"
              data-testid={`layered-telemetry-${layer.key}-idle`}
            >
              <Pause className="w-3 h-3" /> idle
            </span>
          )}
        </div>
        <span
          className={`px-2 py-0.5 text-xs border rounded ${sev.badge}`}
          data-testid={`layered-telemetry-${layer.key}-badge`}
        >
          {sev.label} · {pct.toFixed(1)}%
        </span>
      </div>

      <div className={`relative w-full h-3 rounded ${sev.track} overflow-hidden`}>
        <div
          className={`absolute top-0 left-0 h-full ${sev.bar} transition-all duration-500`}
          style={{ width: `${pct}%` }}
          data-testid={`layered-telemetry-${layer.key}-bar`}
        />
      </div>

      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-400">
        <div>
          <span className="text-gray-500">Slots:</span>{' '}
          <span className="text-white font-mono">
            {layer.slots_in_use}/{layer.slots_total}
          </span>
        </div>
        <div>
          <span className="text-gray-500">Διαθέσιμα:</span>{' '}
          <span className="text-white font-mono">{layer.slots_available}</span>
        </div>
        <div>
          <span className="text-gray-500">Queue:</span>{' '}
          <span className="text-white font-mono">{layer.queue_depth}</span>
        </div>
        {layer.peak_in_flight != null && (
          <div>
            <span className="text-gray-500">Peak:</span>{' '}
            <span className="text-white font-mono">
              {layer.peak_in_flight} ({(layer.peak_saturation_pct || 0).toFixed(1)}%)
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminLayeredTelemetryDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  // v2.13.82 — Export-history state
  const [exportWindow, setExportWindow] = useState('1h');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const pollingRef = useRef(null);

  // v2.13.82 — download persisted telemetry history for the chosen window.
  const handleExportHistory = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/diagnostic/layered-telemetry/history?window=${exportWindow}`,
        { cache: 'no-store' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      if (payload.error || payload.error_type) {
        throw new Error(payload.error_message || payload.error);
      }
      const filename = `telemetry_history_${exportWindow}_${
        new Date().toISOString().replace(/[:.]/g, '-')
      }.json`;
      const blob = new Blob(
        [JSON.stringify(payload, null, 2)],
        { type: 'application/json' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message || String(err));
    } finally {
      setExporting(false);
    }
  }, [exportWindow]);

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/diagnostic/layered-telemetry`,
        { cache: 'no-store' }
      );
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const payload = await res.json();
      if (payload.error_type || payload.error) {
        setError(
          payload.error_type
            ? `${payload.error_type}: ${payload.error_message}`
            : payload.error
        );
      }
      setData(payload);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  useEffect(() => {
    if (!autoRefresh) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return undefined;
    }
    pollingRef.current = setInterval(fetchSnapshot, POLL_INTERVAL_MS);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [autoRefresh, fetchSnapshot]);

  const worstSev = data?.worst_severity || 'ok';
  const worstStyle = severityStyles[worstSev] || severityStyles.ok;
  const WorstIcon = worstStyle.icon;

  return (
    <div className="space-y-4" data-testid="layered-telemetry-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Server className="w-6 h-6 text-blue-400" />
          <div>
            <h2 className="text-xl font-semibold text-white">
              Layered Telemetry Dashboard
            </h2>
            <p className="text-xs text-gray-400">
              Πραγματικός χρόνος κορεσμού — κάθε επίπεδο του pipeline
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label
            className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer"
            data-testid="layered-telemetry-autorefresh-label"
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
              data-testid="layered-telemetry-autorefresh-toggle"
            />
            Auto-refresh (5s)
          </label>
          <button
            onClick={fetchSnapshot}
            disabled={loading}
            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
            data-testid="layered-telemetry-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {/* v2.13.82 — History export */}
          <div className="flex items-center gap-1 border-l border-gray-700 pl-3 ml-1">
            <select
              value={exportWindow}
              onChange={(e) => setExportWindow(e.target.value)}
              disabled={exporting}
              className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="layered-telemetry-export-window"
              title="Telemetry history window"
            >
              <option value="1h">1 ώρα</option>
              <option value="3h">3 ώρες</option>
              <option value="6h">6 ώρες</option>
              <option value="24h">24 ώρες</option>
              <option value="48h">48 ώρες</option>
              <option value="7d">1 εβδομάδα</option>
            </select>
            <button
              onClick={handleExportHistory}
              disabled={exporting}
              className="bg-blue-700 hover:bg-blue-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
              data-testid="layered-telemetry-export-btn"
              title="Download persisted telemetry history as JSON"
            >
              <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
              Export JSON
            </button>
          </div>
        </div>
      </div>

      {/* v2.13.82 — Export error banner */}
      {exportError && (
        <div
          className="bg-red-900/40 border border-red-700 text-red-200 text-sm rounded-lg px-3 py-2"
          data-testid="layered-telemetry-export-error"
        >
          Export απέτυχε: {exportError}
        </div>
      )}

      {/* Headline severity */}
      <div
        className={`flex items-center justify-between bg-gray-800 rounded-lg p-4 border ${
          worstSev === 'critical'
            ? 'border-red-700'
            : worstSev === 'warning'
              ? 'border-orange-700'
              : worstSev === 'elevated'
                ? 'border-amber-700'
                : 'border-emerald-700'
        }`}
        data-testid="layered-telemetry-headline"
      >
        <div className="flex items-center gap-3">
          <WorstIcon className={`w-5 h-5 ${
            worstSev === 'critical'
              ? 'text-red-400'
              : worstSev === 'warning'
                ? 'text-orange-400'
                : worstSev === 'elevated'
                  ? 'text-amber-400'
                  : 'text-emerald-400'
          }`} />
          <div>
            <div className="text-sm text-gray-400">Χειρότερο επίπεδο κορεσμού</div>
            <div className="text-lg font-semibold text-white">
              {worstStyle.label} · {(data?.worst_saturation_pct || 0).toFixed(1)}%
            </div>
          </div>
        </div>
        <div className="text-right text-xs text-gray-400">
          <div>Build: {data?._build_version || '—'}</div>
          <div>
            Τελ. ενημέρωση: {lastUpdate ? lastUpdate.toLocaleTimeString('el-GR') : '—'}
          </div>
        </div>
      </div>

      {/* v2.9.201 — In-flight tracker counter. Surfaces the silent
          killer behind "REAL 97,8 has 1 write in 6h" — when this
          number exceeds ~10% of active stations, the in-flight set
          is leaking and stations are being skipped indefinitely. */}
      {data?.stations_in_flight_count != null && (
        <div
          className="bg-gray-800 rounded-lg p-4 border border-gray-700"
          data-testid="layered-telemetry-in-flight"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-white">
                Stations Currently In-Flight
              </span>
            </div>
            <div className="text-right">
              <span
                className={`text-xl font-mono font-semibold ${
                  data.active_stations_count &&
                  data.stations_in_flight_count >
                    Math.max(5, data.active_stations_count * 0.1)
                    ? 'text-orange-400'
                    : 'text-emerald-400'
                }`}
                data-testid="layered-telemetry-in-flight-count"
              >
                {data.stations_in_flight_count}
              </span>
              {data.active_stations_count != null && (
                <span className="text-xs text-gray-500 ml-1">
                  / {data.active_stations_count} active
                </span>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Αν αυτός ο αριθμός παραμείνει ψηλά (&gt;10% των active stations)
            χωρίς να πέφτει σε νέους κύκλους polling, μπορεί να έχουμε leak στο
            in-flight tracker — οι σταθμοί προσπερνώνται σιωπηλά. Διορθώθηκε στο
            v2.9.201 με auto-cleanup σε κάθε loop restart.
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg flex items-center gap-2"
          data-testid="layered-telemetry-error"
        >
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Layers grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {(data?.layers || []).map((layer) => (
          <LayerRow key={layer.key} layer={layer} />
        ))}
      </div>

      {/* Listener per-host (top saturated) */}
      {data?.listener_per_host_top && data.listener_per_host_top.length > 0 && (
        <div
          className="bg-gray-800 rounded-lg p-4 border border-gray-700"
          data-testid="layered-telemetry-perhost"
        >
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-yellow-400" />
            <h3 className="text-sm font-semibold text-white">
              Listener — Per-Host (Top 10 κορεσμού)
            </h3>
            <span className="text-xs text-gray-400">
              · cap {data.listener_per_host_max}/host · Sat % από rolling 60s window
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-1 px-2">Host</th>
                  <th className="text-right py-1 px-2">In-flight</th>
                  <th className="text-right py-1 px-2" title="Recent peak in the last 60s (drives Sat %)">Recent</th>
                  <th className="text-right py-1 px-2" title="All-time peak since service start">Peak</th>
                  <th className="text-right py-1 px-2">Σύνολο</th>
                  <th className="text-right py-1 px-2">Timeouts</th>
                  <th className="text-right py-1 px-2">Avg s</th>
                  <th className="text-right py-1 px-2">Sat %</th>
                </tr>
              </thead>
              <tbody>
                {data.listener_per_host_top.map((h) => (
                  <tr
                    key={h.host}
                    className="border-b border-gray-700/50 text-gray-200"
                    data-testid={`layered-telemetry-host-${h.host}`}
                  >
                    <td className="py-1 px-2 font-mono truncate max-w-xs">{h.host}</td>
                    <td className="py-1 px-2 text-right font-mono">{h.in_flight}</td>
                    <td
                      className="py-1 px-2 text-right font-mono text-cyan-300"
                      data-testid={`layered-telemetry-recent-peak-${h.host}`}
                    >
                      {h.recent_peak_in_flight ?? '—'}
                    </td>
                    <td className="py-1 px-2 text-right font-mono text-gray-500">{h.peak_in_flight}</td>
                    <td className="py-1 px-2 text-right font-mono">{h.total_requests}</td>
                    <td className="py-1 px-2 text-right font-mono">{h.timeouts}</td>
                    <td className="py-1 px-2 text-right font-mono">{h.avg_seconds}</td>
                    <td
                      className={`py-1 px-2 text-right font-mono ${
                        h.saturation_pct >= 90
                          ? 'text-red-400'
                          : h.saturation_pct >= 75
                            ? 'text-orange-400'
                            : h.saturation_pct >= 50
                              ? 'text-amber-400'
                              : 'text-emerald-400'
                      }`}
                    >
                      {h.saturation_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!data && !error && (
        <div
          className="text-gray-400 text-sm text-center py-8"
          data-testid="layered-telemetry-loading"
        >
          Φόρτωση δεδομένων...
        </div>
      )}
    </div>
  );
};

export default AdminLayeredTelemetryDashboard;
